import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/firebase';

const DesignPreview = ({ projectId, onClick }) => {
  const [designFiles, setDesignFiles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId) return;

    const unsubscribe = onSnapshot(
      query(
        collection(db, `projects/${projectId}/designs`),
        orderBy('uploadedAt', 'desc')
      ),
      (snapshot) => {
        if (!snapshot.empty) {
          const files = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              type: 'design',
              title: data.name,
              subtitle: data.comment || 'No comment',
              timestamp: data.uploadedAt ? (data.uploadedAt.toDate ? data.uploadedAt.toDate() : new Date(data.uploadedAt)) : null,
              // data.name is a user-editable display title, not reliably the real filename
              // (e.g. renamed to "Ground Floor Plan Rev 3" with no extension) — check the
              // real MIME type first, then fall back to the real fileName saved at upload
              // time, same fallback chain DrawingViewer/DesignLanding use.
              fileType: data.fileType === 'application/pdf' ? 'pdf'
                : data.fileType?.startsWith('image/') ? 'img'
                : (data.fileName || data.name || '').includes('.')
                  ? (data.fileName || data.name).split('.').pop()?.toLowerCase()
                  : '',
              url: data.url
            };
          });
          setDesignFiles(files);
        } else {
          setDesignFiles([]);
        }
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching design files:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [projectId]);

  const getTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    const now = new Date();
    const diffInMinutes = Math.floor((now - timestamp) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  if (loading) {
    return (
      <div style={{
        height: '120px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--branding-app-surface-panel-hint)',
        fontSize: '14px'
      }}>
        Loading...
      </div>
    );
  }

  if (designFiles.length === 0) {
    return (
      <div style={{
        height: '120px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--branding-app-surface-panel-hint)',
        fontSize: '14px'
      }}>
        No design files
      </div>
    );
  }

  return (
    <div 
      style={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        background: 'transparent',
        padding: '16px',
        transition: 'all 0.2s ease'
      }}
    >
      <div style={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        paddingRight: '8px'
      }}>
      {designFiles.map((file, index) => (
          <div 
            key={file.id}
            style={{ 
              display: 'flex', 
              alignItems: 'flex-start', 
              gap: '12px', 
              marginBottom: index < designFiles.length - 1 ? '12px' : '0',
              padding: '4px 0'
            }}
          >
            {/* Icon */}
            <div style={{
              width: '28px',
              height: '28px',
              background: file.fileType === 'pdf' ? '#fef2f2' :
                         file.fileType === 'doc' || file.fileType === 'docx' ? 'var(--branding-app-primary-soft)' :
                         file.fileType === 'dwg' ? '#fef3c7' :
                         '#f0fdf4',
              border: file.fileType === 'pdf' ? '1px solid #fecaca' :
                      file.fileType === 'doc' || file.fileType === 'docx' ? '1px solid var(--branding-app-primary)' :
                      file.fileType === 'dwg' ? '1px solid #fde68a' :
                      '1px solid #bbf7d0',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '10px',
              fontWeight: '600',
              color: file.fileType === 'pdf' ? '#dc2626' :
                     file.fileType === 'doc' || file.fileType === 'docx' ? 'var(--branding-app-primary)' :
                     file.fileType === 'dwg' ? '#d97706' :
                     '#16a34a',
              flexShrink: 0
            }}>
              {file.fileType === 'pdf' ? 'PDF' :
               file.fileType === 'doc' || file.fileType === 'docx' ? 'DOC' :
               file.fileType === 'dwg' ? 'DWG' :
               'IMG'}
            </div>
            
            {/* Content */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: '13px',
                fontWeight: '600',
                color: 'var(--branding-app-surface-panel-fg)',
                marginBottom: '2px',
                lineHeight: '1.3',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {file.title}
              </div>
              
              <div style={{
                fontSize: '12px',
                color: 'var(--branding-app-surface-panel-sub)',
                marginBottom: '2px',
                lineHeight: '1.3',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {file.subtitle}
              </div>
              
              <div style={{
                fontSize: '11px',
                color: 'var(--branding-app-surface-panel-hint)'
              }}>
                {getTimeAgo(file.timestamp)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DesignPreview;
